import React, { useRef, useState } from 'react'

import { Container, ContainerTable, TableFiltersHeader } from './styles'
import { Input, Select, Table } from '~/components/shared'
import { IAllergy } from '~/interfaces'
import { message } from '~/services'
import { transformDate } from '~/utils'
import { useNormalizedFilter } from '~/hooks'

interface AllergiesListProps {
  allergies: IAllergy[]
  isLoading: boolean
  error: string
}

export const AllergiesList = ({ allergies, isLoading, error }: AllergiesListProps): JSX.Element => {
  const { filter } = useNormalizedFilter()
  const inputFilter = useRef<HTMLInputElement>(null)
  const [allowRefresh, setAllowRefresh] = useState(false)
  const [descriptionFilter, setDescriptionFilter] = useState('')
  const [selectedType, setSelectedType] = useState('')

  const filterByDescription = (allergies: IAllergy[]): IAllergy[] => {
    const isFilterEmpty = descriptionFilter === ''
    if (isFilterEmpty) return allergies

    return filter(allergies, { searchTerm: descriptionFilter, filterKey: 'description' })
  }

  const filterByType = (allergies: IAllergy[]): IAllergy[] => {
    const isFilterEmpty = selectedType === 'Todos'
    if (isFilterEmpty) return allergies

    if (selectedType === 'Outro') {
      const allergiesTypes = ['Alimentação', 'Ambiente', 'Contato', 'Droga']
      return allergies.filter(allergy => !allergiesTypes.includes(allergy.type))
    }

    return allergies.filter(allergy => allergy.type.includes(selectedType))
  }

  const parseAllergiesToRowData = (allergies: IAllergy[]): IAllergy[] => {
    const filterAllergies = filterByType(filterByDescription(allergies))
    return filterAllergies.map(allergy => ({ ...allergy, date: transformDate({ date: allergy.date }) }))
  }

  const handleSearch = text => {
    if (text.target.value !== '') {
      setAllowRefresh(true)
    }
    setDescriptionFilter(text.target.value)
  }

  const setSelectedItem = value => {
    if (value !== 'Todos') {
      setAllowRefresh(true)
    }
    setSelectedType(value as string)
  }

  const handleRefresh = () => {
    setDescriptionFilter('')
    setSelectedType('Todos')
    parseAllergiesToRowData(allergies)
    inputFilter.current!.value = ''
    setAllowRefresh(false)
  }

  return (
    <Container>
      <ContainerTable>
        <TableFiltersHeader>
          <Select
            name="select"
            placeholder="Todos"
            maxHeight="250px"
            setValue={setSelectedItem}
            value={selectedType}
            options={[
              { label: 'Todos', value: 'Todos' },
              { label: 'Alimentação', value: 'Alimentação' },
              { label: 'Ambiente', value: 'Ambiente' },
              { label: 'Contato', value: 'Contato' },
              { label: 'Droga', value: 'Droga' },
              { label: 'Outro', value: 'Outro' }
            ]}
          />

          <Input
            icon="search"
            ref={inputFilter}
            message=""
            name="search_allergies"
            placeholder="Pesquisar alergia"
            onChange={handleSearch}
          />
        </TableFiltersHeader>

        <Table
          isLoading={isLoading}
          data={parseAllergiesToRowData(allergies)}
          messageError={error || message('allergies.search_not_found')}
          headerLineHeight="30px"
          headerPadding="10px 0 10px 24px"
          minHeight="0px"
          onRefresh={allowRefresh ? handleRefresh : undefined}
          model={[
            {
              key: 'description',
              label: message('allergies.name').toUpperCase(),
              sortable: true,
              minWidth: '200px'
            },
            {
              key: 'type',
              label: message('allergies.type').toUpperCase(),
              sortable: true,
              width: '18%',
              minWidth: '100px'
            },
            {
              key: 'class',
              label: message('allergies.class').toUpperCase(),
              sortable: true,
              width: '18%',
              minWidth: '100px'
            },
            {
              key: 'date',
              label: message('allergies.date').toUpperCase(),
              sortable: true,
              width: '18%',
              minWidth: '100px'
            }
          ]}
        />
      </ContainerTable>
    </Container>
  )
}
